import React, { useState, useEffect, ChangeEvent } from "react";
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../../authContext';
import { signUpWithPhone } from '../../../api/login.service';
import { Mixpanel } from '../../../mixpanel/MixpanelTrack';
import { EVENTS } from '../../../mixpanel/mixpanelEvent';
import { IoMdPhonePortrait } from 'react-icons/io';

function ModernSignIn() {
  const navigate = useNavigate();
  const { email, setEmail } = useAuth();
  const [phoneIsLoading, setPhoneIsLoading] = useState(false);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isShowSignUpPage, setisShowSignUpPage] = useState(true);
  const [countdown, setCountdown] = useState(10);

  const handlePhoneChange = (event: ChangeEvent<HTMLInputElement>) => {
    setPhoneNumber(event.target.value);
  };


  const isValidPhone = (phone: string): boolean => {
    const phoneRegex = /^\+?[1-9]\d{1,14}$/;
    return phoneRegex.test(phone.replace(/\s/g, ''));
  };


  const handlePhoneLogin = async () => {
    if (!isValidPhone(phoneNumber)) {
      alert('Please enter a valid phone number with country code (e.g., +1234567890)');
      return;
    }

    setPhoneIsLoading(true);
    
    try {
      await signUpWithPhone(phoneNumber);
      sessionStorage.setItem("logintype", "phone");
      sessionStorage.setItem("phone", phoneNumber);
      setEmail(phoneNumber);
      
      Mixpanel.set({email: phoneNumber, name: phoneNumber}, phoneNumber);
      Mixpanel.track(EVENTS.Login_page_loaded, { requestEmail: phoneNumber, loginType: 'phone'});
      
      navigate('/signin/otp');
    } catch (error) {
      console.error('Phone signup error:', error);
      alert('Failed to send verification code. Please try again.');
    } finally {
      setPhoneIsLoading(false);
    }
  };


  useEffect(() => {
    if (!isShowSignUpPage) {
      const intervalId = setInterval(() => {
        setCountdown((prevCountdown) => (prevCountdown > 0 ? prevCountdown - 1 : 0));
      }, 1000);

      return () => clearInterval(intervalId);
    }
  }, [!isShowSignUpPage]);

  useEffect(() => {
    const loginType = localStorage.getItem("logintype");
    const urlParams = new URLSearchParams(window.location.search);
    const code = urlParams.get('code');
   
    const fetchData = async () => {
      if (code && !email) {
        setisShowSignUpPage(false);
        // Handle OAuth callbacks here
      }      
    }        
    fetchData();
  }, [email]);

  if (!isShowSignUpPage) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex items-center justify-center p-4">
        <div className="max-w-md w-full">
          <div className="bg-white rounded-2xl shadow-xl p-8 text-center">
            <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
              <div className="w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-2">Loading...</h2>
            <p className="text-gray-600 mb-4">Please wait while we process your request</p>
            {countdown > 0 && (
              <p className="text-sm text-gray-500">Continuing in {countdown} seconds...</p>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-purple-50 flex">
      {/* Left Side - Login Form */}
      <div className="flex-1 flex items-center justify-center p-8">
        <div className="max-w-md w-full">
          {/* Logo */}
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-gray-800 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <span className="text-white font-bold text-2xl">W</span>
            </div>
            <h1 className="text-3xl font-bold text-gray-900 mb-2">frontdesk24</h1>
            <p className="text-gray-600">Welcome back! Please sign in to your account</p>
          </div>

          {/* WhatsApp Number Login Form */}
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  WhatsApp Number
                </label>
                <div className="relative">
                  <IoMdPhonePortrait className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
                  <input
                    type="tel"
                    value={phoneNumber}
                    onChange={handlePhoneChange}
                    placeholder="+1234567890"
                    className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all duration-200"
                  />
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Enter your WhatsApp number with country code
                </p>
              </div>

              <button 
                onClick={handlePhoneLogin}
                disabled={!isValidPhone(phoneNumber) || phoneIsLoading}
                className={`w-full py-3 px-4 rounded-xl font-medium transition-all duration-200 ${
                  isValidPhone(phoneNumber) && !phoneIsLoading
                    ? 'bg-blue-600 hover:bg-blue-700 text-white shadow-lg hover:shadow-xl' 
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {phoneIsLoading ? (
                  <div className="flex items-center justify-center">
                    <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin mr-2"></div>
                    Sending Code...
                  </div>
                ) : (
                  'Send Verification Code'
                )}
              </button>

              {/* Provider Text */}
              <p className="text-xs text-gray-500 text-center mt-4">
                Provided by PVOD SERVICES PRIVATE LIMITED
              </p>
            </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-sm text-gray-600">
              Don't have an account?{' '}
              <button className="text-blue-600 hover:text-blue-700 font-medium">
                Sign up here
              </button>
            </p>
          </div>
        </div>
      </div>

      {/* Right Side - Hero Image */}
      <div className="hidden lg:flex lg:flex-1 bg-gradient-to-br from-blue-600 to-purple-700 relative overflow-hidden">
        <div className="absolute inset-0 bg-black opacity-20"></div>
        <div className="relative z-10 flex items-center justify-center p-12">
          <div className="text-center text-white">
            <div className="w-32 h-32 bg-white bg-opacity-20 rounded-full flex items-center justify-center mx-auto mb-8">
              <IoMdPhonePortrait className="w-16 h-16 text-white" />
            </div>
            <h2 className="text-4xl font-bold mb-4">Manage Your WhatsApp Business</h2>
            <p className="text-xl text-blue-100 mb-8 max-w-md">
              Connect with customers, automate messages, and grow your business with our powerful WhatsApp management platform.
            </p>
            <div className="grid grid-cols-2 gap-6 max-w-sm mx-auto">
              <div className="text-center">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-2xl">📱</span>
                </div>
                <p className="text-sm text-blue-100">Easy Setup</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-2xl">🚀</span>
                </div>
                <p className="text-sm text-blue-100">Fast & Reliable</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-2xl">🔒</span>
                </div>
                <p className="text-sm text-blue-100">Secure</p>
              </div>
              <div className="text-center">
                <div className="w-12 h-12 bg-white bg-opacity-20 rounded-lg flex items-center justify-center mx-auto mb-2">
                  <span className="text-2xl">📊</span>
                </div>
                <p className="text-sm text-blue-100">Analytics</p>
              </div>
            </div>
          </div>
        </div>
        
        {/* Decorative Elements */}
        <div className="absolute top-10 right-10 w-20 h-20 bg-white bg-opacity-10 rounded-full"></div>
        <div className="absolute bottom-20 left-10 w-16 h-16 bg-white bg-opacity-10 rounded-full"></div>
        <div className="absolute top-1/2 right-20 w-12 h-12 bg-white bg-opacity-10 rounded-full"></div>
      </div>
    </div>
  );
}

export default ModernSignIn;
